import { useState } from "react";
import type { NextPage } from "next";
import Image from "next/image";
import Link from "next/link";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";

import { NotionService } from "@acme/api/services";
import type { BlogPost } from "@acme/api/website";

import PageLayout from "~/components/layout/PageLayout";
import Button from "~/components/ui-elements/Button";
import type { RouterOutputs } from "~/utils/api";

interface Blogs {
  blogs: RouterOutputs["blog"]["getBlogs"];
}

const Blog: NextPage<Blogs> = (props) => {
  const blogs = props.blogs;
  const loading: BlogPost = {
    id: "loading",
    properties: {
      title: "Loading...",
      description: "",
      cover: { url: "" },
      tags: [],
      publishDate: "",
    },
  };

  return (
    <>
      <PageLayout
        seo={{
          title: "Blog",
          description: "Our vision for Vital and more",
        }}
      >
        <div className="dark relative flex h-[500px] w-full animate-fade flex-col items-center justify-center">
          <div className="absolute inset-0 z-10 h-full w-full bg-gradient-to-tr from-black/20 to-black/60" />
          <Image
            src="/images/blog/vital-gradient.png"
            alt={"vital confidence score blog preview banner"}
            fill
            className="z-0 block animate-fade object-cover"
            sizes="100vw"
            quality={100}
          />
          {blogs[0] ? (
            <FeaturedBlog blog={blogs[0]} />
          ) : (
            <FeaturedBlog blog={loading} />
          )}
        </div>
        {blogs && <BlogList blogs={blogs} />}
      </PageLayout>
    </>
  );
};

const FeaturedBlog = ({ blog }: { blog: BlogPost }) => (
  <div className="z-10 flex w-full max-w-screen-xl flex-col justify-start gap-4 p-8 text-base-content lg:p-12 xl:p-16 2xl:p-0">
    <h1 className="text-5xl font-semibold opacity-80">
      {blog.properties.title}
    </h1>
    <h4 className="font-regular mb-1 max-w-[480px] text-xl opacity-80">
      {blog.properties.description}
    </h4>
    <div className="flex flex-row gap-1">
      <div
        className={classnames(
          "flex flex-row items-center gap-1.5 px-4 py-2 font-medium",
          "rounded-md bg-base-content/10 backdrop-blur-md",
        )}
      >
        New
        <FontAwesomeIcon icon={faSparkles} className="h-3.5 w-3.5" />
      </div>
      <Button
        variant={"ghost"}
        className="h-10 rounded-sm"
        rightIcon={faArrowRight}
      >
        <Link href={`/blog/${blog.id}`}>Read more</Link>
      </Button>
    </div>
  </div>
);

const BlogList = ({ blogs }: { blogs: BlogPost[] }) => (
  <div className="flex w-full max-w-screen-xl flex-col gap-4 px-8 pb-64 pt-32 lg:px-12 xl:px-16 2xl:px-0">
    <h1 className="text-2xl font-medium">All articles</h1>
    <div className="group/grid grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 ">
      {blogs.map((post) => (
        <BlogCard key={post.id} post={post} />
      ))}
    </div>
  </div>
);

export const getCoverImage = (title: string) => {
  switch (title.toLowerCase()) {
    case "our story":
      return "/images/blog/cortina.jpg";
    case "omega-3 fatty acids":
      return "/images/blog/sushi.jpg";
    case "the total vitality score":
      return "/images/blog/total-vitality-score.jpg";
    case "the confidence score":
      return "/images/blog/confidence-score.jpg";
    case "measure your health from home":
      return "/images/blog/measure-health-from-home.jpg";
    case "100 year vision":
      return "/images/blog/100-year-vision.jpg";
    default:
      return "/images/blog/vital-gradient.png";
  }
};

export const getCoverImageDark = (title: string) => {
  switch (title.toLowerCase()) {
    case "our story":
      return "/images/blog/cortina-dark.jpg";
    case "omega-3 fatty acids":
      return "/images/blog/sushi-dark.jpg";
    case "the total vitality score":
      return "/images/blog/total-vitality-score-dark.jpg";
    case "the confidence score":
      return "/images/blog/confidence-score-dark.jpg";
    case "measure your health from home":
      return "/images/blog/measure-health-from-home-dark.jpg";
    case "100 year vision":
      return "/images/blog/100-year-vision.jpg";
    default:
      return "/images/blog/vital-gradient-dark.png";
  }
};

const BlogCard = ({ post }: { post: BlogPost }) => (
  <Link href={`/blog/${post.id}`} key={post.id}>
    <div
      className={classnames(
        "relative aspect-square overflow-hidden rounded-2xl transition-all duration-150",
        "group/self border-[1px] border-base-200 bg-base-100 hover:shadow-lg",
      )}
    >
      <CoverImage
        url={getCoverImage(post.properties.title)}
        darkUrl={getCoverImageDark(post.properties.title)}
        title={post.properties.title}
      />
      <div className="relative z-10 flex h-1/2 w-full flex-col justify-between gap-2 p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          <PostTags tags={post.properties.tags} />
          <p className="overflow-scroll-y h-12 overflow-ellipsis text-sm font-normal opacity-60">
            {post.properties.description}
          </p>
        </div>
        <p className="self-end text-xs font-light text-base-content/50">
          {formatDate(post.properties.publishDate)}
        </p>
      </div>
    </div>
  </Link>
);

const CoverImage = ({
  url,
  darkUrl,
  title,
}: {
  url: string;
  darkUrl: string;
  title: string;
}) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative z-0 h-1/2 w-full">
      <Image
        src={url}
        alt={title}
        fill
        className={classnames(
          "object-cover transition-all duration-300 group-hover/grid:grayscale group-hover/self:grayscale-0 dark:hidden",
          loaded ? "opacity-100" : "opacity-0",
        )}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        onLoad={() => setLoaded(true)}
      />
      <Image
        src={darkUrl}
        alt={title}
        fill
        className={classnames(
          "hidden object-cover transition-all duration-300 group-hover/grid:grayscale group-hover/self:grayscale-0 dark:block",
          loaded ? "opacity-100" : "opacity-0",
        )}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

const PostTags = ({ tags }: { tags: string[] }) => (
  <div className="font-xs flex flex-row gap-1 overflow-x-scroll">
    {tags.map((tag) => (
      <p
        key={tag}
        className="whitespace-nowrap rounded-md bg-base-200 px-2 py-0.5 text-xs font-normal uppercase tracking-wide"
      >
        {tag}
      </p>
    ))}
  </div>
);

const formatDate = (date: string) => {
  const parts = date.split("-");
  const year = parts[0];
  const month = parts[2];
  const day = parts[1];

  return `${day}/${month}/${year}`;
};

export const getStaticProps = async () => {
  const blogs = await NotionService.getBlogPosts();
  return { props: { blogs } };
};

export default Blog;
